import React, { useMemo, useState } from 'react';
import './Checkout.css';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Country, State } from 'country-state-city';
import { useSearchParams } from 'react-router-dom';

const PLAN = {
  name: 'Plan Mensual',
  priceLabel: '180.000',
  currency: 'ARS',
  period: '/ mes',
  unitPrice: 180000,
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://backend.thethingapp.com';

const Checkout = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const checkoutTypeRaw = String(searchParams.get('type') || 'monthly').trim().toLowerCase();
  const checkoutType = checkoutTypeRaw === 'setup' ? 'setup' : 'monthly';

  const activePlan = useMemo(() => {
    if (checkoutType === 'setup') {
      return {
        name: 'Implementación inicial',
        priceLabel: '300.000',
        currency: 'ARS',
        period: '',
        unitPrice: 300000,
      };
    }
    return PLAN;
  }, [checkoutType]);

  const [billing, setBilling] = useState({
    firstName: '',
    lastName: '',
    company: '',
    countryIso2: 'AR',
    country: 'Argentina',
    email: '',
    phone: '',
    address1: '',
    address2: '',
    city: '',
    provinceIso2: '',
    province: '',
    postalCode: '',
  });

  const [paymentMethod, setPaymentMethod] = useState('mercadopago');
  const [submitting, setSubmitting] = useState(false);

  const total = useMemo(() => activePlan.unitPrice, [activePlan.unitPrice]);

  const setField = (key, value) => {
    setBilling((prev) => ({ ...prev, [key]: value }));
  };

  const validate = () => {
    if (!billing.firstName.trim()) return 'Ingresá tu nombre.';
    if (!billing.lastName.trim()) return 'Ingresá tu apellido.';
    if (!billing.countryIso2 || !billing.country.trim()) return 'Ingresá tu país/región.';
    if (!billing.email.trim()) return 'Ingresá tu email.';
    if (!billing.phone.trim()) return 'Ingresá tu teléfono.';
    if (!billing.address1.trim()) return 'Ingresá tu dirección.';
    if (!billing.city.trim()) return 'Ingresá tu ciudad.';
    if (!billing.province.trim()) return 'Ingresá tu provincia.';
    if (!billing.postalCode.trim()) return 'Ingresá tu código postal.';
    if (paymentMethod !== 'mercadopago') return 'Seleccioná un método de pago.';
    return null;
  };

  const [couponOpen, setCouponOpen] = useState(false);
  const [couponCode, setCouponCode] = useState('');

  const allCountries = useMemo(() => {
    const list = Country.getAllCountries() || [];
    return list.sort((a, b) => String(a.name).localeCompare(String(b.name), 'es'));
  }, []);

  const countryStates = useMemo(() => {
    if (!billing.countryIso2) return [];
    return State.getStatesOfCountry(billing.countryIso2) || [];
  }, [billing.countryIso2]);

  const handleCountryChange = (iso2) => {
    const selected = allCountries.find((c) => c.isoCode === iso2);
    setBilling((prev) => ({
      ...prev,
      countryIso2: iso2,
      country: selected?.name || '',
      provinceIso2: '',
      province: '',
      city: '',
    }));
  };

  const handleProvinceChange = (stateIso2) => {
    const selected = countryStates.find((s) => s.isoCode === stateIso2);
    setBilling((prev) => ({
      ...prev,
      provinceIso2: stateIso2,
      province: selected?.name || '',
      city: '',
    }));
  };

  const goToDemoGateway = () => {
    const order = {
      plan: activePlan,
      total,
      billing,
      paymentMethod,
      paymentType: checkoutType,
    };

    try {
      sessionStorage.setItem('tt_checkout_order', JSON.stringify(order));
    } catch (e) {
    }

    navigate('/checkout/mercadopago-demo', { state: order });
  };

  const handlePlaceOrder = async () => {
    if (!currentUser) {
      toast.error('Tenés que iniciar sesión para continuar.');
      return;
    }

    const validationError = validate();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setSubmitting(true);

    try {
      const res = await fetch(`${BACKEND_URL}/api/create-payment-preference`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: currentUser.uid,
          billing,
          paymentType: checkoutType,
        }),
      });

      const data = await res.json().catch(() => ({}));

      const checkoutUrl = data?.checkout_url || data?.sandbox_init_point || data?.init_point;

      if (data?.success && checkoutUrl) {
        window.location.href = checkoutUrl;
        return;
      }

      goToDemoGateway();
    } catch (err) {
      goToDemoGateway();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="checkout-page">
      <div className="checkout-header">
        <h1 className="checkout-title">Finalizar compra</h1>
        <p className="checkout-subtitle">Completá tus datos y pagá con MercadoPago</p>
      </div>

      <div className="checkout-grid">
        <div className="checkout-card">
          <h2 className="checkout-card-title">Datos de facturación</h2>

          <div className="checkout-form-grid">
            <div className="checkout-field">
              <label>Nombre *</label>
              <input value={billing.firstName} onChange={(e) => setField('firstName', e.target.value)} />
            </div>
            <div className="checkout-field">
              <label>Apellidos *</label>
              <input value={billing.lastName} onChange={(e) => setField('lastName', e.target.value)} />
            </div>

            <div className="checkout-field checkout-field-full">
              <label>Nombre de la empresa (opcional)</label>
              <input value={billing.company} onChange={(e) => setField('company', e.target.value)} />
            </div>

            <div className="checkout-field checkout-field-full">
              <label>País / Región *</label>
              <select value={billing.countryIso2} onChange={(e) => handleCountryChange(e.target.value)}>
                {allCountries.map((c) => (
                  <option key={c.isoCode} value={c.isoCode}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="checkout-field checkout-field-full">
              <label>Dirección de la calle *</label>
              <input value={billing.address1} onChange={(e) => setField('address1', e.target.value)} />
            </div>

            <div className="checkout-field checkout-field-full">
              <label>Apartamento, habitación, etc. (opcional)</label>
              <input value={billing.address2} onChange={(e) => setField('address2', e.target.value)} />
            </div>

            <div className="checkout-field checkout-field-full">
              <label>Población *</label>
              <div className="checkout-autocomplete">
                <input
                  value={billing.city}
                  onChange={(e) => setField('city', e.target.value)}
                  placeholder="Ingresá tu ciudad"
                />
              </div>
            </div>

            <div className="checkout-field checkout-field-full">
              <label>Región / Provincia *</label>
              {countryStates.length > 0 ? (
                <select value={billing.provinceIso2} onChange={(e) => handleProvinceChange(e.target.value)}>
                  <option value="">Seleccionar...</option>
                  {countryStates.map((s) => (
                    <option key={s.isoCode} value={s.isoCode}>
                      {s.name}
                    </option>
                  ))}
                </select>
              ) : (
                <input value={billing.province} onChange={(e) => setField('province', e.target.value)} />
              )}
            </div>

            <div className="checkout-field checkout-field-full">
              <label>Código postal / Zip *</label>
              <input value={billing.postalCode} onChange={(e) => setField('postalCode', e.target.value)} />
            </div>

            <div className="checkout-field checkout-field-full">
              <label>Teléfono *</label>
              <input value={billing.phone} onChange={(e) => setField('phone', e.target.value)} />
            </div>

            <div className="checkout-field checkout-field-full">
              <label>Dirección de correo electrónico *</label>
              <input type="email" value={billing.email} onChange={(e) => setField('email', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="checkout-side">
          <div className="checkout-stack">
            <div className="checkout-card">
              <h2 className="checkout-card-title">Tu pedido</h2>

              <div className="checkout-order-table">
                <div className="checkout-order-header">
                  <span>Producto</span>
                  <span>Subtotal</span>
                </div>

                <div className="checkout-order-row">
                  <span>{activePlan.name} × 1</span>
                  <span>$ {activePlan.priceLabel},00</span>
                </div>

                <div className="checkout-order-totals">
                  <div className="checkout-order-total-row">
                    <span>Subtotal</span>
                    <span>$ {activePlan.priceLabel},00</span>
                  </div>
                  <div className="checkout-order-total-row checkout-order-grand">
                    <span>Total</span>
                    <span>$ {activePlan.priceLabel},00</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="checkout-coupon-card" onClick={() => setCouponOpen((v) => !v)} role="button" tabIndex={0}>
              <p className="checkout-coupon-title">¿Tienes un cupón? Haz clic aquí para introducir tu código de cupón</p>
              {couponOpen && (
                <div className="checkout-coupon-body" onClick={(e) => e.stopPropagation()}>
                  <input
                    className="checkout-coupon-input"
                    placeholder="Código de cupón"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                  />
                  <button
                    className="checkout-coupon-apply"
                    type="button"
                    onClick={() => toast.info('Cupón aplicado (demo).')}
                  >
                    Aplicar
                  </button>
                </div>
              )}
            </div>

            <div className="checkout-card">
              <p className="checkout-payments-title">Medios de pago disponibles en Mercado Pago</p>

              <label className={`checkout-payment-option ${paymentMethod === 'mercadopago' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="paymentMethod"
                  value="mercadopago"
                  checked={paymentMethod === 'mercadopago'}
                  onChange={() => setPaymentMethod('mercadopago')}
                />
                <div className="checkout-payment-copy">
                  <p className="checkout-payment-name">MercadoPago</p>
                  <p className="checkout-payment-desc">Tarjeta, transferencia o dinero en cuenta</p>
                </div>
              </label>

              <button
                className="checkout-place-order"
                onClick={handlePlaceOrder}
                disabled={submitting}
              >
                {submitting ? <span className="checkout-spinner" /> : 'Realizar el pedido'}
              </button>

              <p className="checkout-secure-note">
                Tus datos personales se utilizarán para procesar tu pedido.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
